import * as Fire from 'firebase/firestore';

export type QueryBasicFilters = {
  limit?: number;
  orderBy?: [string, Fire.OrderByDirection];
  startAt?: unknown;
  endAt?: unknown;
  startAfter?: unknown;
  endBefore?: unknown;
};

export type QueryBuildFn = (q: QueryBuilder) => QueryBuilder | undefined;

export type QueryFilterEntry = {
  when: boolean | (() => boolean);
  value?: QueryBuildFn;
};

export type QueryWhereParams = { field: string; op: Fire.WhereFilterOp; value: unknown };

export type QueryFilterSchema = QueryBuildFn | Array<QueryBuildFn | QueryFilterEntry>;

export type QueryBuildResponse = { query: Fire.Query<Fire.DocumentData>; debug: any[] };

class QueryBuilder {
  private database: Fire.Firestore;
  // private collection: Fire.CollectionReference | undefined;
  collectionName: string;
  constraints: Fire.QueryConstraint[] = [];
  //
  filters: QueryBasicFilters = {};
  wheres: QueryWhereParams[] = [];
  ors: QueryWhereParams[][] = [];
  ands: QueryWhereParams[][] = [];
  private __debugger: any[] = [];
  private hasOrderBy?: boolean;
  private withDebugger = false;

  constructor(db: Fire.Firestore, collectionName: string) {
    this.database = db;
    this.collectionName = collectionName;
  }

  buildQuery(isCollectionGroup = false): QueryBuildResponse {
    if (!this.collectionName) {
      throw new Error('collection not defined');
    }
    const params: Array<Fire.QueryConstraint | Fire.QueryCompositeFilterConstraint> = [];

    if (this.filters.orderBy && !this.hasOrderBy) {
      const [field, direction] = this.filters.orderBy;
      this.__debugger.push({ type: 'orderBy', value: [field, direction] });
      params.push(Fire.orderBy(field, direction));
    }

    const wheres = this.wheres.map((entry) => {
      this.__debugger.push({ type: 'where', value: `${entry.field} ${entry.op} ${entry.value}` });
      return Fire.where(entry.field, entry.op, entry.value);
    });

    const ors = this.ors.map((or) => {
      this.__debugger.push({ type: 'or', value: or });
      return Fire.or(...or.map((entry) => Fire.where(entry.field, entry.op, entry.value)));
    });

    const ands = this.ands.map((and) => {
      this.__debugger.push({ type: 'and', value: and });
      return Fire.and(...and.map((entry) => Fire.where(entry.field, entry.op, entry.value)));
    });

    if ((this.ors.length || this.ands.length) && this.wheres.length) {
      const and = Fire.and(...wheres, ...ors, ...ands);
      params.push(and);
    } else {
      if (this.wheres.length) params.push(...wheres);
      if (this.ands.length) params.push(...ands);
      if (this.ors.length) params.push(...ors);
    }

    if (this.filters.startAt) {
      params.push(Fire.startAt(this.filters.startAt));
    }
    if (this.filters.endAt) {
      params.push(Fire.endAt(this.filters.endAt));
    }
    if (this.filters.startAfter) {
      params.push(Fire.startAfter(this.filters.startAfter));
    }
    if (this.filters.endBefore) {
      params.push(Fire.endBefore(this.filters.endBefore));
    }

    const limit = this.filters.limit ?? 25;
    if (limit) {
      params.push(Fire.limit(limit));
      this.__debugger.push({ type: 'limit', value: limit });
    }

    let collection: Fire.CollectionReference | Fire.Query;

    if (isCollectionGroup) {
      collection = Fire.collectionGroup(this.database, this.collectionName);
    } else {
      collection = Fire.collection(this.database, this.collectionName);
    }

    const query = params.length
      ? Fire.query(collection, ...(params as Fire.QueryConstraint[]))
      : Fire.query(collection);

    if (this.withDebugger) {
      console.info(this.collectionName, this.__debugger, query);
    }

    return { query, debug: this.__debugger };
  }

  disableSort() {
    this.hasOrderBy = false;
  }

  enableDebugger() {
    this.withDebugger = true;
  }

  queryDoc(id: string) {
    return Fire.doc(this.database, this.collectionName, id);
  }

  querySubDoc(parentID: string, subcollection: string, id: string) {
    return Fire.doc(this.database, this.collectionName, parentID, subcollection, id);
  }

  endBefore(input: unknown): QueryBuilder {
    if (typeof input === 'undefined') return this;
    this.filters.endBefore = input; //Fire.endBefore(input);
    return this;
  }

  startAfter(input: unknown): QueryBuilder {
    if (typeof input === 'undefined') return this;
    this.filters.startAfter = input; //Fire.startAfter(input);
    return this;
  }

  startAt(input: unknown): QueryBuilder {
    if (typeof input === 'undefined') return this;
    this.filters.startAt = input; //Fire.startAt(input);
    return this;
  }

  endAt(input: unknown): QueryBuilder {
    if (typeof input === 'undefined') return this;
    this.filters.endAt = input; //Fire.endAt(input);
    return this;
  }

  byCreatedAt(direction: Fire.OrderByDirection = 'desc'): QueryBuilder {
    return this.orderBy('createdAt', direction);
  }

  orderBy(field: string | undefined, direction: Fire.OrderByDirection = 'desc'): QueryBuilder {
    if (!field) return this;
    this.filters.orderBy = [field, direction];
    return this;
  }

  limit(input?: number): QueryBuilder {
    this.filters.limit = input;
    return this;
  }

  where(field: string, op: Fire.WhereFilterOp, value: unknown): QueryBuilder {
    if (typeof value === 'undefined') return this;
    this.wheres.push({ field, op, value });
    return this;
  }

  or(fn: (q: QueryBuilder) => QueryBuilder) {
    const child = new QueryBuilder(this.database, this.collectionName);
    const build = fn(child);
    if (!build.wheres.length) return this;
    this.ors.push(build.wheres);
  }

  and(fn: (q: QueryBuilder) => QueryBuilder) {
    const child = new QueryBuilder(this.database, this.collectionName);
    const build = fn(child);
    if (!build.wheres.length) return this;
    this.ands.push(build.wheres);
  }
}

export default QueryBuilder;
